/* halo-toolcart.js · PixelNova Solutions · 10/8/2026
   Added: accessible scoped panels and awaited Shopify discount updates. */
function cartToolClose(element) {
  const drawer = element.closest('#halo-cart-sidebar');
  const popup = element.closest('.popup-toolDown');
  popup?.classList.remove('show');
  drawer?.querySelector('.previewCart')?.classList.remove('active-tool');
  const opener = drawer?.querySelector(`[data-popup="${popup?.id}"]`);
  opener?.setAttribute('aria-expanded', 'false');
  opener?.focus();
}

class CartTool extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.querySelectorAll('.cartTool-item').forEach(button => {
      button.addEventListener('click', () => {
        const drawer = this.closest('#halo-cart-sidebar');
        const popup = drawer?.querySelector(`#${button.dataset.popup}`);
        if (!popup) return;
        drawer.querySelectorAll('.popup-toolDown.show').forEach(p => p.classList.remove('show'));
        this.querySelectorAll('[aria-expanded]').forEach(b => b.setAttribute('aria-expanded', 'false'));
        popup.classList.add('show');
        button.setAttribute('aria-expanded', 'true');
        drawer.querySelector('.previewCart')?.classList.add('active-tool');
        popup.querySelector('input,textarea,select,button')?.focus({preventScroll:true});
      });
    });
  }
}

class CartCancel extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.querySelector('button')?.addEventListener('click', () => cartToolClose(this));
  }
}

class CartNote extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.querySelector('[data-update-note]')?.addEventListener('click', async event => {
      const button = event.currentTarget;
      if (button.disabled) return;
      button.disabled = true;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/update.js`, {
          method:'POST', headers:{'Content-Type':'application/json'},
          body:JSON.stringify({note:this.querySelector('.text-area').value})
        });
        if (!response.ok) throw new Error('Save failed');
        cartToolClose(this);
      } catch (_) {
        button.textContent = 'Try again';
      } finally { button.disabled = false; }
    });
  }
}

function cartDiscountCodes(cart) {
  if (Array.isArray(cart.discount_codes)) return cart.discount_codes.map(d => d.code).filter(Boolean);
  const allocations = [ ...(cart.cart_level_discount_applications || []),
    ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(d => d.discount_application)) ];
  return [...new Set(allocations.filter(d => d?.type === 'discount_code').map(d => d.title))];
}

class CouponCode extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.input = this.querySelector('input[name="discount"]');
    this.button = this.querySelector('[data-update-coupon]');
    this.status = this.querySelector('[data-coupon-status]');
    if (!this.input || !this.button || !this.status) return;
    if (CouponCode.notice) this.message(CouponCode.notice.text, CouponCode.notice.error);
    this.button.addEventListener('click', () => this.apply());
    this.input.addEventListener('keydown', event => {
      if (event.key === 'Enter') { event.preventDefault(); this.apply(); }
      if (event.key === 'Escape' && !this.busy) { event.preventDefault(); cartToolClose(this); }
    });
    this.input.addEventListener('input', () => {
      CouponCode.notice = null;
      this.message('');
    });
  }
  message(text, error = false) {
    this.status.textContent = text;
    this.status.dataset.error = String(error);
  }
  async apply() {
    if (this.busy) return;
    const code = this.input.value.trim();
    if (!code) { this.message('Enter a discount code.', true); this.input.focus(); return; }
    this.busy = true;
    this.button.disabled = true;
    this.input.disabled = true;
    this.button.textContent = 'Applying…';
    this.message('');
    try {
      const root = window.Shopify?.routes?.root || '/';
      const currentResponse = await fetch(`${root}cart.js`, {cache:'no-store'});
      if (!currentResponse.ok) throw new Error('Read failed');
      const current = await currentResponse.json();
      const existing = cartDiscountCodes(current);
      const codes = [...existing.filter(c => c.toLowerCase() !== code.toLowerCase()), code];
      const response = await fetch(`${root}cart/update.js`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({discount:codes.join(',')})
      });
      if (!response.ok) throw new Error('Update failed');
      const cart = await response.json();
      const entry = cart.discount_codes?.find(d => d.code?.toLowerCase() === code.toLowerCase());
      const accepted = entry ? entry.applicable : cartDiscountCodes(cart).some(c => c.toLowerCase() === code.toLowerCase());
      if (!accepted) {
        this.message('This code couldn’t be applied to this cart.', true);
        return;
      }
      try { localStorage.removeItem('storedDiscount'); } catch (_) {}
      CouponCode.notice = {text:`${code} applied.`, error:false};
      this.message(CouponCode.notice.text);
      cartToolClose(this);
      if (window.sharedFunctions?.updateSidebarCart) window.sharedFunctions.updateSidebarCart(cart);
      else window.location.reload();
    } catch (_) {
      this.message('Couldn’t confirm the code. Check your cart before trying again.', true);
    } finally {
      this.busy = false;
      this.button.disabled = false;
      this.input.disabled = false;
      this.button.textContent = 'Apply';
    }
  }
}

class CartDiscountRemove extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    const button = this.querySelector('button');
    const status = this.querySelector('[role="status"]');
    button?.addEventListener('click', async () => {
      if (button.disabled) return;
      button.disabled = true;
      status.textContent = '';
      try {
        const root = window.Shopify?.routes?.root || '/';
        const currentResponse = await fetch(`${root}cart.js`, {cache:'no-store'});
        if (!currentResponse.ok) throw new Error('Read failed');
        const current = await currentResponse.json();
        const codes = cartDiscountCodes(current).filter(code => code.toLowerCase() !== this.dataset.code.toLowerCase());
        const response = await fetch(`${root}cart/update.js`, {
          method:'POST', headers:{'Content-Type':'application/json'},
          body:JSON.stringify({discount:codes.join(',')})
        });
        if (!response.ok) throw new Error('Update failed');
        const cart = await response.json();
        if (cartDiscountCodes(cart).some(code => code.toLowerCase() === this.dataset.code.toLowerCase())) throw new Error('Not removed');
        CouponCode.notice = null;
        try { localStorage.removeItem('storedDiscount'); } catch (_) {}
        if (window.sharedFunctions?.updateSidebarCart) window.sharedFunctions.updateSidebarCart(cart);
        else window.location.reload();
      } catch (_) {
        status.textContent = 'Couldn’t confirm removal. Check your cart before trying again.';
      } finally { button.disabled = false; }
    });
  }
}

customElements.define('cart-discount-remove', CartDiscountRemove);
customElements.define('cart-item-tool', CartTool);
customElements.define('cart-cancel-popup', CartCancel);
customElements.define('cart-note', CartNote);
customElements.define('coupon-code', CouponCode);
